/* A.J. Martin wishlist (2026-10-19) — the storefront half of the self-hosted wishlist that replaced
   Swym Wishlist Plus. Server: ajmartin-pricer (server/wishlist.js behind the App Proxy riders
   /apps/ajm-custom-order/wl/*). This file does four things:
   1. Hearts: any `.ajm-wish` button (snippets/ajm-wish-heart.liquid on product pages and theme
      cards; the USF card template in assets/usf.js for collection and search grids) toggles the
      piece. One delegated click handler, so hearts USF renders later work too. Hearts are painted
      from a per-browser cache first, then from the server's `state` call on every page.
   2. The header count bubble, `.ajm-wish-count` (snippets/header.icons.liquid).
   3. The note under the product price for a few seconds after a save (buttons with data-wish-note).
   4. The wishlist page (sections/ajm-wishlist.liquid): the shopper's own list, or a friend's shared
      list when the URL carries ?share=TOKEN.
   Identity: a signed-in shopper is known to the server through the App Proxy's signed customer id;
   a guest holds the token the server minted on their first save (localStorage 'ajm-wl-guest-v1'),
   sent in every request body. The server says what to do with it (guestToken = store it,
   retireGuestToken = forget it, after the list merged into an account). Nothing here reads Liquid's
   `customer`: the store uses hosted customer accounts, where it is not a reliable signal.
   Matomo events, category "Wishlist": Add / Remove (name = the piece), Share on, Email saved. */
(function () {
  'use strict';
  if (window.AJMWishlist) return;

  var cfg = window.AJMWishlistConfig || {};
  var BASE = cfg.base || '/apps/ajm-custom-order/wl';
  var PAGE_URL = cfg.pageUrl || '/pages/wishlist';
  var ACCOUNT_URL = cfg.accountUrl || '/account';
  var SHOP_URL = cfg.shopUrl || '/collections/all-products';
  var TOKEN_KEY = 'ajm-wl-guest-v1';
  var CACHE_KEY = 'ajm-wl-cache-v1';
  var LINKED_KEY = 'ajm-wl-linked-v1'; // this browser has been signed in with a list at least once
  var NOTE_MS = 9000;
  var TOAST_MS = 5000;
  // The A.J. Martin Workroom's /track — the same endpoint and ids as the virtual try-on and the
  // recommendation clicks, so the Workroom's Analytics reads the three together (bots and staff
  // dropped the same way). No personal data: the per-tab session id, the per-browser visitor id
  // only when the shop's privacy settings allow analytics, the piece, and where the heart was.
  var RELAY = cfg.relay || 'https://apps.ajmartinonline.com';

  var state = { loggedIn: false, ids: {}, count: 0, loaded: false };

  /* ---- storage (private mode and blocked storage throw; the list still works for the page) ---- */
  function read(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function write(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* ignore */ } }
  function drop(key) { try { localStorage.removeItem(key); } catch (e) { /* ignore */ } }
  function token() { return read(TOKEN_KEY) || ''; }

  function applyIds(items) {
    state.ids = {};
    (items || []).forEach(function (it) { state.ids[String(it.productId)] = true; });
    state.count = items ? items.length : 0;
    state.loaded = true;
    write(CACHE_KEY, JSON.stringify({ ids: Object.keys(state.ids), t: Date.now() }));
  }
  function loadCache() {
    try {
      var c = JSON.parse(read(CACHE_KEY) || 'null');
      if (!c || !Array.isArray(c.ids)) return;
      state.ids = {};
      c.ids.forEach(function (id) { state.ids[String(id)] = true; });
      state.count = c.ids.length;
    } catch (e) { /* ignore */ }
  }

  /* ---- Matomo user id (2026-10-25, the Client Panel's Customers report): the pricer's `state`
     envelope carries `analyticsId`, an opaque 24-hex code it derives from the signed-in customer
     (never the email or the Shopify id), null when signed out. With the shop's privacy settings
     allowing analytics (the ajm-vid rule) Matomo gets it as the user id and localStorage keeps it,
     so head.matomo.liquid can attribute the next page view itself; otherwise both are cleared. ---- */
  var MTM_UID_KEY = 'ajm-mtm-uid';
  function matomoUser(id) {
    var allowed = true;
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      allowed = !cp || typeof cp.analyticsProcessingAllowed !== 'function' || cp.analyticsProcessingAllowed();
    } catch (e) { allowed = false; }
    var paq = window._paq = window._paq || [];
    if (allowed && typeof id === 'string' && /^[0-9a-f]{24}$/.test(id)) {
      paq.push(['setUserId', id]);
      write(MTM_UID_KEY, id);
    } else {
      if (read(MTM_UID_KEY)) paq.push(['resetUserId']);
      drop(MTM_UID_KEY);
    }
  }

  /* ---- the proxy ---- */
  function api(path, body) {
    var payload = Object.assign({}, body || {});
    var t = token();
    if (t) payload.guestToken = t;
    return fetch(BASE + '/' + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error((json && json.error) || 'Something went wrong — please try again.');
          err.status = res.status;
          throw err;
        }
        if (json.guestToken) write(TOKEN_KEY, json.guestToken);
        if (json.retireGuestToken) drop(TOKEN_KEY);
        if (typeof json.loggedIn === 'boolean') state.loggedIn = json.loggedIn;
        if ('analyticsId' in json) matomoUser(json.analyticsId);
        if (Array.isArray(json.items) && path !== 'list') applyIds(json.items);
        if (path === 'list' && Array.isArray(json.items)) applyIds(json.items);
        paint();
        return json;
      });
    });
  }

  /* ---- hearts and the header bubble ---- */
  function isSaved(productId) { return !!state.ids[String(productId)]; }
  function paint() {
    var hearts = document.querySelectorAll('.ajm-wish[data-product-id]');
    for (var i = 0; i < hearts.length; i++) {
      var saved = isSaved(hearts[i].getAttribute('data-product-id'));
      hearts[i].classList.toggle('is-saved', saved);
      hearts[i].setAttribute('aria-pressed', saved ? 'true' : 'false');
    }
    var bubbles = document.querySelectorAll('.ajm-wish-count');
    for (var j = 0; j < bubbles.length; j++) {
      var num = bubbles[j].querySelector('.cart-link__bubble-num') || bubbles[j];
      num.textContent = String(state.count);
      bubbles[j].classList.toggle('cart-link__bubble--visible', state.count > 0);
    }
    try { document.dispatchEvent(new CustomEvent('ajm:wishlist', { detail: { count: state.count, ids: Object.keys(state.ids), loggedIn: state.loggedIn } })); } catch (e) { /* old browser */ }
  }

  function itemFromButton(btn) {
    var d = btn.dataset;
    var price = d.price === undefined || d.price === '' ? null : Number(d.price);
    return {
      productId: d.productId,
      variantId: d.variantId || '',
      title: d.title || '',
      price: price === null || isNaN(price) ? null : price,
      url: d.url || '',
      handle: d.handle || '',
      imageUrl: d.image || '',
      sku: d.sku || ''
    };
  }

  function track(action, name) {
    try {
      window._paq = window._paq || [];
      window._paq.push(['trackEvent', 'Wishlist', action, name || '']);
    } catch (e) { /* ignore */ }
  }

  function newId() {
    var abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', out = '';
    var bytes = crypto.getRandomValues(new Uint8Array(12));
    for (var i = 0; i < bytes.length; i++) out += abc[bytes[i] % 62];
    return out;
  }
  /* relay(ev, piece, extra): one event to the Workroom. ev is wl-save / wl-remove / wl-page /
     wl-click / wl-share / wl-email / wl-signin; extra.where says which heart (product, collection,
     search, recommendation, page, card) and extra.detail the rest (guest|account, own|shared|empty,
     on|copy, page|note). Never throws, never awaited. */
  function relay(ev, piece, extra) {
    var sid = '', vid = '';
    try {
      sid = sessionStorage.getItem('ajm-sid') || '';
      if (!sid) { sid = newId(); sessionStorage.setItem('ajm-sid', sid); }
    } catch (e) { return; }
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      var allowed = !cp || typeof cp.analyticsProcessingAllowed !== 'function' || cp.analyticsProcessingAllowed();
      if (allowed) {
        vid = localStorage.getItem('ajm-vid') || '';
        if (!vid) { vid = newId(); localStorage.setItem('ajm-vid', vid); }
      }
    } catch (e) { vid = ''; }
    var phone = false;
    try { phone = !matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) { /* desktop */ }
    var e = { ev: ev };
    if (extra && extra.where) e.where = extra.where;
    if (extra && extra.detail) e.detail = extra.detail;
    var body = JSON.stringify({
      sid: sid, vid: vid || undefined, device: phone ? 'phone' : 'desktop',
      ua: String(navigator.userAgent || '').slice(0, 200),
      auto: navigator.webdriver === true || undefined,
      product: { id: String((piece && (piece.productId || piece.id)) || ''), title: String((piece && piece.title) || '').slice(0, 120), kind: '' },
      events: [e]
    });
    try {
      fetch(RELAY + '/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true, mode: 'cors', credentials: 'include' }).catch(function () {});
    } catch (err) { /* ignore */ }
  }
  function whereOf(btn) {
    if (!btn || !btn.closest) return 'card';
    if (btn.closest('.ajm-price-line')) return 'product';
    if (btn.closest('[data-ajm-wishlist-page]')) return 'page';
    if (btn.closest('[class^="usf-"], [class*=" usf-"], .usf-sr-container')) return 'search';
    if (btn.closest('product-recommendations, [data-section-type="product-recommendations"], .ajm-wedding-bands')) return 'recommendation';
    if (btn.closest('[data-section-type="collection-template"], [data-section-type="collection-grid"], .collection-grid, #CollectionSection')) return 'collection';
    return 'card';
  }

  function toggle(btn) {
    if (btn.getAttribute('data-busy')) return Promise.resolve();
    var item = itemFromButton(btn);
    if (!item.productId) return Promise.resolve();
    var wasSaved = isSaved(item.productId);
    btn.setAttribute('data-busy', '1');
    btn.classList.toggle('is-saved', !wasSaved);
    btn.setAttribute('aria-pressed', wasSaved ? 'false' : 'true');
    var call = wasSaved
      ? api('remove', { productId: item.productId })
      : api('add', { item: item });
    var hasNote = btn.hasAttribute('data-wish-note');
    return call.then(function () {
      track(wasSaved ? 'Remove' : 'Add', item.title);
      relay(wasSaved ? 'wl-remove' : 'wl-save', item, { where: whereOf(btn), detail: state.loggedIn ? 'account' : 'guest' });
      // The product page's own note (with the email field) is the acknowledgement there; every
      // other heart — collection cards, search grids, quick shop — gets the toast, as does a removal.
      if (!wasSaved && hasNote) showNote(btn);
      else toast(wasSaved ? 'removed' : 'saved', item);
    }).catch(function (err) {
      paint(); // back to the server's truth
      var msg = err.message || 'Could not save — please try again.';
      if (hasNote) showNote(btn, msg);
      else toast('error', item, msg);
    }).then(function () { btn.removeAttribute('data-busy'); });
  }

  /* The note under the product price (2026-10-15 mockup, kept): signed in, or a guest who already
     gave an email → "we'll email you if its price drops"; a guest without one gets a small email
     field right there. */
  var noteTimer;
  function showNote(btn, errorText) {
    var line = btn.closest('.ajm-price-line') || btn.parentNode;
    if (!line) return;
    var note = line.parentNode.querySelector('.ajm-wish-note');
    if (!note) {
      note = document.createElement('div');
      note.className = 'ajm-wish-note';
      note.setAttribute('role', 'status');
      line.insertAdjacentElement('afterend', note);
    }
    clearTimeout(noteTimer);
    note.hidden = false;
    if (errorText) {
      note.innerHTML = '<b>' + esc(errorText) + '</b>';
      noteTimer = setTimeout(function () { note.hidden = true; }, NOTE_MS);
      return;
    }
    var wantsEmail = !state.loggedIn && !state.hasEmail;
    note.innerHTML = '<b>Saved to your wishlist.</b> '
      + (wantsEmail
        ? 'Add your email and we\'ll let you know if its price drops.'
          + '<form class="ajm-wish-note__form"><input type="email" required placeholder="Your email" aria-label="Your email" autocomplete="email"><button type="submit">Notify me</button></form>'
        : 'We\'ll email you if its price drops.')
      + ' <a href="' + PAGE_URL + '">View your wishlist</a>';
    if (!wantsEmail) noteTimer = setTimeout(function () { note.hidden = true; }, NOTE_MS);
    var form = note.querySelector('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = form.querySelector('input');
        api('email', { email: input.value }).then(function () {
          state.hasEmail = true;
          track('Email saved');
          relay('wl-email', null, { detail: 'note' });
          note.innerHTML = '<b>Thank you.</b> We\'ll email you if its price drops. <a href="' + PAGE_URL + '">View your wishlist</a>';
          noteTimer = setTimeout(function () { note.hidden = true; }, NOTE_MS);
        }).catch(function (err) {
          input.setCustomValidity(err.message || 'Please check the address');
          input.reportValidity();
          setTimeout(function () { input.setCustomValidity(''); }, 2000);
        });
      });
    }
  }

  /* The acknowledgement (2026-10-19, owner: Swym opened a side panel on every save — not wanted, but
     "an acknowledgement that something's been added would be useful"): one small card at the foot of
     the screen for a few seconds — the piece's photo and name, what happened, and a link to the list.
     One element, reused; a second save replaces the text and restarts the clock. */
  var toastEl, toastTimer;
  function toast(kind, item, errorText) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ajm-wish-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    var title = item && item.title ? esc(item.title) : '';
    var thumb = item && item.imageUrl && kind !== 'error'
      ? '<img class="ajm-wish-toast__img" src="' + esc(item.imageUrl) + '" alt="" width="44" height="44">' : '';
    var head = kind === 'saved' ? 'Saved to your wishlist.'
      : kind === 'removed' ? 'Removed from your wishlist.'
      : (errorText || 'Could not save — please try again.');
    toastEl.innerHTML = thumb
      + '<span class="ajm-wish-toast__body"><b>' + esc(head) + '</b>'
      + (title ? '<span class="ajm-wish-toast__title">' + title + '</span>' : '')
      + (kind === 'saved' ? '<a class="ajm-wish-toast__link" href="' + PAGE_URL + '" data-wl-open>View your wishlist</a>' : '')
      + '</span>'
      + '<button type="button" class="ajm-wish-toast__close" aria-label="Dismiss">&times;</button>';
    toastEl.setAttribute('data-kind', kind);
    toastEl.querySelector('.ajm-wish-toast__close').addEventListener('click', hideToast);
    clearTimeout(toastTimer);
    // Two frames so a replaced toast re-runs its entrance.
    toastEl.classList.remove('is-shown');
    var raf = window.requestAnimationFrame || function (f) { setTimeout(f, 16); };
    raf(function () { raf(function () { toastEl.classList.add('is-shown'); }); });
    toastTimer = setTimeout(hideToast, TOAST_MS);
  }
  function hideToast() {
    clearTimeout(toastTimer);
    if (toastEl) toastEl.classList.remove('is-shown');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.ajm-wish[data-product-id]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn);
  }, true);

  // Grids render after load (USF, quick-shop modals, recommendations): repaint when hearts appear.
  var paintTimer;
  var observer = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1 && (n.matches('.ajm-wish') || n.querySelector('.ajm-wish, .ajm-wish-count'))) {
          clearTimeout(paintTimer);
          paintTimer = setTimeout(paint, 50);
          return;
        }
      }
    }
  });

  /* ---- helpers ---- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) {
    if (n == null || isNaN(n)) return '';
    var whole = Math.abs(n - Math.round(n)) < 0.005;
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
  }
  function imageAt(url, width) {
    if (!url) return '';
    // Shopify CDN images take a width parameter; anything else is left alone.
    if (/cdn\.shopify\.com/.test(url)) return url.replace(/([?&])width=\d+/, '$1').replace(/\?$/, '') + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    return url;
  }

  /* ---- the wishlist page ---- */
  var liveCache = {};
  var liveMissing = {}; // handles the store answered 404 for (not a busy or failed lookup)
  function liveProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!liveCache[handle]) {
      liveCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (r.status === 404) liveMissing[handle] = true; return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return liveCache[handle];
  }

  /* Similar pieces for a sold one (2026-10-19, owner pick 5): Shopify's own related recommendations,
     which the Workroom fills from its "You may also like" picks (the related_products metafield).
     Pieces still for sale and not already on the list; none → nothing drawn. */
  var similarCache = {};
  function similarTo(productId, limit) {
    if (!productId) return Promise.resolve([]);
    if (!similarCache[productId]) {
      similarCache[productId] = fetch('/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=6&intent=related', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return (d && d.products) || []; })
        .catch(function () { return []; });
    }
    return similarCache[productId].then(function (list) {
      return list.filter(function (p) { return p && p.available && !isSaved(p.id); }).slice(0, limit);
    });
  }
  function similarHtml(products, compact) {
    if (!products.length) return '';
    return '<div class="ajm-wl-similar' + (compact ? ' ajm-wl-similar--compact' : '') + '"><p class="ajm-wl-similar__label">Sold — similar pieces</p><ul>'
      + products.map(function (p) {
        var img = p.featured_image ? '<img src="' + esc(imageAt(p.featured_image, 200)) + '" alt="" loading="lazy">' : '<span class="ajm-wl-card__noimg"></span>';
        return '<li><a href="' + esc(p.url || ('/products/' + p.handle)) + '" data-similar="' + esc(p.id) + '">' + img
          + '<span class="ajm-wl-similar__title">' + esc(p.title) + '</span>'
          + (typeof p.price === 'number' ? '<span class="ajm-wl-similar__price">' + esc(money(p.price / 100)) + '</span>' : '') + '</a></li>';
      }).join('') + '</ul></div>';
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-similar]');
    if (a) { track('Similar piece', a.textContent.trim()); relay('wl-click', { productId: a.getAttribute('data-similar'), title: a.textContent.trim() }, { detail: 'similar' }); }
  }, true);

  function cardHtml(item, opts) {
    var href = item.url || (item.handle ? '/products/' + item.handle : '#');
    // A friend's shared list (opts.verify) waits for the store's own title and photo: what is stored
    // is what the sharer's browser sent, so a forged call could otherwise put any wording on the
    // shop's domain (2026-10-19 security review). See verifySharedCard.
    var img = item.imageUrl && !opts.verify ? '<img src="' + esc(imageAt(item.imageUrl, 600)) + '" alt="" loading="lazy">' : '<span class="ajm-wl-card__noimg"></span>';
    var fromShop = item.addedBy === 'shop';
    return '<li class="ajm-wl-card"' + (opts.verify ? ' hidden' : '') + ' data-item-id="' + esc(item.id) + '" data-product-id="' + esc(item.productId) + '" data-handle="' + esc(item.handle) + '" data-variant-id="' + esc(item.variantId) + '">'
      + '<a class="ajm-wl-card__image" href="' + esc(href) + '">' + img + '<span class="ajm-wl-card__badge" hidden></span></a>'
      + '<div class="ajm-wl-card__body">'
      + (fromShop ? '<p class="ajm-wl-card__from">Picked for you by A.J. Martin</p>' : '')
      + '<h3 class="ajm-wl-card__title"><a href="' + esc(href) + '">' + (opts.verify ? '' : esc(item.title)) + '</a></h3>'
      + '<p class="ajm-wl-card__price">' + (item.priceAtSave != null ? '<span class="ajm-wl-card__now">' + esc(money(item.priceAtSave)) + '</span>' : '') + '</p>'
      + (fromShop && item.staffNote ? '<p class="ajm-wl-card__note">“' + esc(item.staffNote) + '”</p>' : '')
      + (opts.readOnly ? '' : '<button type="button" class="ajm-wl-card__remove" data-remove="' + esc(item.productId) + '">Remove</button>')
      + '</div></li>';
  }

  /* A shared list's card: the store's title and photo, or no card at all when the store has no such
     piece. A lookup that failed for another reason (busy, offline) falls back to the stored title,
     which a visitor can't arrange on someone else's browser. Resolves to whether the card stays. */
  function verifySharedCard(card, item) {
    return liveProduct(item.handle).then(function (p) {
      if (!p && (liveMissing[item.handle] || !item.handle)) { card.remove(); return false; }
      var link = card.querySelector('.ajm-wl-card__title a');
      link.textContent = p && p.title ? p.title : item.title;
      var src = p ? p.featured_image : item.imageUrl;
      if (src) card.querySelector('.ajm-wl-card__image').innerHTML = '<img src="' + esc(imageAt(src, 600)) + '" alt="" loading="lazy"><span class="ajm-wl-card__badge" hidden></span>';
      card.hidden = false;
      return true;
    });
  }

  // Live price and availability from the store itself, so a sold or reduced piece reads right.
  function patchCardLive(card, item) {
    liveProduct(item.handle).then(function (p) {
      if (!p) return;
      var variant = null;
      if (item.variantId && Array.isArray(p.variants)) {
        for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(item.variantId)) variant = p.variants[i];
      }
      var available = variant ? variant.available : p.available;
      var cents = variant ? variant.price : p.price;
      var now = typeof cents === 'number' ? cents / 100 : null;
      var badge = card.querySelector('.ajm-wl-card__badge');
      var priceEl = card.querySelector('.ajm-wl-card__price');
      var imgEl = card.querySelector('.ajm-wl-card__image img');
      if (!imgEl && p.featured_image) {
        card.querySelector('.ajm-wl-card__image').innerHTML = '<img src="' + esc(imageAt(p.featured_image, 600)) + '" alt="" loading="lazy"><span class="ajm-wl-card__badge" hidden></span>';
        badge = card.querySelector('.ajm-wl-card__badge');
      }
      if (available === false) {
        card.classList.add('is-sold');
        badge.textContent = 'Sold';
        badge.hidden = false;
        similarTo(item.productId, 3).then(function (list) {
          var body = card.querySelector('.ajm-wl-card__body');
          if (body && list.length && !card.querySelector('.ajm-wl-similar')) body.insertAdjacentHTML('beforeend', similarHtml(list, false));
        });
      }
      if (now != null) {
        var was = item.priceAtSave;
        if (was != null && now < was - 0.005) {
          priceEl.innerHTML = '<span class="ajm-wl-card__now ajm-wl-card__now--drop">' + esc(money(now)) + '</span> <s class="ajm-wl-card__was">' + esc(money(was)) + '</s> <span class="ajm-wl-card__drop">Price reduced</span>';
        } else {
          priceEl.innerHTML = '<span class="ajm-wl-card__now">' + esc(money(now)) + '</span>';
        }
      }
    });
  }

  /* "Ask about these" (2026-10-19, owner pick 4): a question about the saved pieces, or a request to
     see them in person. Lands in the shop's Inbox as a message from the website; the pieces travel
     with it. The honeypot field is off-screen, never type=hidden (bots skip those). */
  function askHtml() {
    return '<div class="ajm-wl-ask" id="ask">'
      + '<div class="ajm-wl-ask__buttons">'
      + '<button type="button" class="ajm-wl-btn ajm-wl-ask__open" data-ask-mode="ask" aria-expanded="false">Ask about these pieces</button>'
      + '<button type="button" class="ajm-wl-btn ajm-wl-btn--outline ajm-wl-ask__open" data-ask-mode="visit" aria-expanded="false">See them in person</button>'
      + '</div>'
      + '<form class="ajm-wl-ask__form" hidden novalidate>'
      + '<p class="ajm-wl-ask__intro"></p>'
      + '<div class="ajm-wl-ask__row"><label><span>Your name</span><input type="text" name="name" required maxlength="120" autocomplete="name"></label>'
      + '<label><span>Email</span><input type="email" name="email" maxlength="200" autocomplete="email"></label>'
      + '<label><span>Phone <em>(optional)</em></span><input type="tel" name="phone" maxlength="40" autocomplete="tel"></label></div>'
      + '<label class="ajm-wl-ask__when" hidden><span>When suits you</span><input type="text" name="when" maxlength="200" placeholder="Saturday morning, or Tuesday early afternoon"></label>'
      + '<label><span>Your question <em>(optional)</em></span><textarea name="message" rows="3" maxlength="1500"></textarea></label>'
      + '<label class="ajm-wl-hp" aria-hidden="true">Website<input type="text" name="website" tabindex="-1" autocomplete="off"></label>'
      + '<div class="ajm-wl-ask__actions"><button type="submit" class="ajm-wl-btn">Send</button><span class="ajm-wl-ask__status" role="status"></span></div>'
      + '</form></div>';
  }
  function wireAsk(root) {
    var box = root.querySelector('.ajm-wl-ask');
    if (!box) return;
    var form = box.querySelector('form');
    var mode = 'ask';
    function open(m) {
      mode = m;
      form.hidden = false;
      box.querySelectorAll('.ajm-wl-ask__open').forEach(function (b) { b.setAttribute('aria-expanded', b.getAttribute('data-ask-mode') === m ? 'true' : 'false'); });
      form.querySelector('.ajm-wl-ask__intro').textContent = m === 'visit'
        ? 'We will set the pieces aside and find a time that suits you.'
        : 'Ask us anything about these pieces — sizing, history, a closer look. We reply within a day.';
      form.querySelector('.ajm-wl-ask__when').hidden = m !== 'visit';
      form.querySelector('input[name="name"]').focus();
    }
    box.addEventListener('click', function (e) {
      var b = e.target.closest('.ajm-wl-ask__open');
      if (b) open(b.getAttribute('data-ask-mode'));
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var status = form.querySelector('.ajm-wl-ask__status');
      var send = form.querySelector('button[type="submit"]');
      var v = function (n) { var el = form.querySelector('[name="' + n + '"]'); return el ? el.value.trim() : ''; };
      status.className = 'ajm-wl-ask__status';
      if (!v('name')) { status.textContent = 'Please tell us your name.'; status.classList.add('is-error'); return; }
      if (!v('email') && !v('phone')) { status.textContent = 'Please leave an email or a phone number so we can reply.'; status.classList.add('is-error'); return; }
      send.disabled = true;
      status.textContent = 'Sending…';
      api('ask', { name: v('name'), email: v('email'), phone: v('phone'), message: v('message'), when: v('when'), inPerson: mode === 'visit', website: v('website') }).then(function () {
        box.innerHTML = '<p class="ajm-wl-ask__done">Thank you — we have your message and will be in touch within a day.</p>';
        track(mode === 'visit' ? 'Viewing request' : 'Question');
        relay('wl-ask', null, { detail: mode });
      }).catch(function (err) {
        status.textContent = err.message || 'Could not send — please try again.';
        status.classList.add('is-error');
        send.disabled = false;
      });
    });
    if (location.hash === '#ask') { open('ask'); box.scrollIntoView({ block: 'start' }); }
  }

  /* Sharing has no switch (2026-10-19, owner: the checkbox was irrelevant — Copy, Share… and Email
     each turn sharing on). The link is made the first time it is needed; "Make list private" retires
     it for good (the next share makes a new one) and is shown only while a link exists. */
  function showShareState(root, on, url) {
    var input = root.querySelector('.ajm-wl-share__link input');
    if (input) input.value = on ? (url || '') : '';
    var priv = root.querySelector('.ajm-wl-share__private');
    if (priv) priv.hidden = !on;
  }
  function ensureShared(root) {
    var input = root.querySelector('.ajm-wl-share__link input');
    if (input && input.value) return Promise.resolve(input.value);
    return api('share', { enabled: true }).then(function (r) {
      showShareState(root, !!r.shareEnabled, r.shareUrl);
      if (r.shareEnabled) { track('Share on'); relay('wl-share', null, { detail: 'on' }); }
      return r.shareUrl || '';
    });
  }

  /* A shopper following a saved piece to its product page (own list or a shared one). Bound once
     per root; the capture phase so it fires before the navigation. */
  function wirePieceClicks(root, which) {
    if (root.getAttribute('data-wl-clicks')) return;
    root.setAttribute('data-wl-clicks', '1');
    root.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('.ajm-wl-card a[href]');
      if (a) {
        var card = a.closest('.ajm-wl-card');
        var titleEl = card && card.querySelector('.ajm-wl-card__title');
        relay('wl-click', { productId: card && card.getAttribute('data-product-id'), title: titleEl ? titleEl.textContent.trim() : '' }, { detail: which });
        return;
      }
      var signin = e.target.closest && e.target.closest('.ajm-wl-tool a.ajm-wl-btn[href]');
      if (signin && signin.getAttribute('href') === ACCOUNT_URL) relay('wl-signin', null, { detail: 'page' });
    }, true);
  }

  function renderPage(root) {
    var copy = root.dataset;
    var params = new URLSearchParams(location.search);
    var shareToken = params.get('share');
    root.innerHTML = '<p class="ajm-wl-loading">Loading…</p>';

    if (shareToken) {
      fetch(BASE + '/shared?token=' + encodeURIComponent(shareToken), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (view) {
          if (!view) {
            root.innerHTML = '<div class="ajm-wl-empty"><p>' + esc(copy.sharedMissing || 'This wishlist is not available.') + '</p>'
              + '<a class="ajm-wl-btn" href="' + esc(SHOP_URL) + '">' + esc(copy.shopLabel || 'Browse the collection') + '</a></div>';
            return;
          }
          var who = view.ownerFirstName ? view.ownerFirstName + '’s wishlist' : (copy.sharedHeading || 'A shared wishlist');
          root.innerHTML = '<div class="ajm-wl-head"><h2 class="ajm-wl-heading">' + esc(who) + '</h2>'
            + '<p class="ajm-wl-count"></p></div>'
            + '<ul class="ajm-wl-grid">' + view.items.map(function (it) { return cardHtml(it, { readOnly: true, verify: true }); }).join('') + '</ul>'
            + '<div class="ajm-wl-foot"><a class="ajm-wl-btn ajm-wl-btn--outline" href="' + esc(PAGE_URL) + '">' + esc(copy.ownLabel || 'Start your own wishlist') + '</a></div>';
          var checks = [].map.call(root.querySelectorAll('.ajm-wl-card'), function (card, i) {
            return verifySharedCard(card, view.items[i]).then(function (kept) { if (kept) patchCardLive(card, view.items[i]); return kept; });
          });
          Promise.all(checks).then(function (kept) {
            var n = kept.filter(Boolean).length;
            root.querySelector('.ajm-wl-count').textContent = n + (n === 1 ? ' piece' : ' pieces');
          });
          relay('wl-page', null, { detail: 'shared' });
          wirePieceClicks(root, 'shared');
        })
        .catch(function () { root.innerHTML = '<div class="ajm-wl-empty"><p>Could not load this wishlist — please try again.</p></div>'; });
      return;
    }

    api('list').then(function (list) {
      state.hasEmail = !!list.hasEmail;
      var items = list.items || [];
      var html = '';
      html += '<div class="ajm-wl-head">'
        + '<h2 class="ajm-wl-heading">' + esc(copy.heading || 'Your wishlist') + '</h2>'
        + '<p class="ajm-wl-count">' + (items.length ? items.length + (items.length === 1 ? ' piece saved' : ' pieces saved') : '') + '</p>'
        + '</div>';
      if (!items.length) {
        html += '<div class="ajm-wl-empty"><p>' + esc(copy.empty || 'Nothing saved yet. Tap the heart on any piece to keep it here.') + '</p>'
          + '<a class="ajm-wl-btn" href="' + esc(SHOP_URL) + '">' + esc(copy.shopLabel || 'Browse the collection') + '</a></div>';
      } else {
        html += '<ul class="ajm-wl-grid">' + items.map(function (it) { return cardHtml(it, {}); }).join('') + '</ul>';
        html += askHtml();
      }
      // The tools strip only when there is a tool to show — an empty strip is a pair of rules.
      var tools = '';
      /* Signed in (2026-10-19, owner): the box stays, with a greyed "Linked to your account" in place
         of the button, so a shopper can see the list has synced. A browser that was signed in before
         but is not now shows a guest list that is NOT the account's — so it says to sign in again. */
      if (list.loggedIn) write(LINKED_KEY, '1');
      var wasLinked = !list.loggedIn && read(LINKED_KEY) === '1';
      if (list.loggedIn) {
        tools += '<div class="ajm-wl-tool"><p class="ajm-wl-tool__title">' + esc(copy.guestTitle || 'Keep it on every device') + '</p>'
          + '<p>' + esc(copy.linkedText || 'Your list is saved to your account and follows you to every device.') + '</p>'
          + '<span class="ajm-wl-btn ajm-wl-btn--linked" role="status" aria-disabled="true"><span aria-hidden="true">✓</span> ' + esc(copy.linkedLabel || 'Linked to your account') + '</span></div>';
      } else {
        tools += '<div class="ajm-wl-tool"><p class="ajm-wl-tool__title">' + esc(copy.guestTitle || 'Keep it on every device') + '</p>'
          + '<p>' + esc(wasLinked ? (copy.relinkText || 'You have signed in on this device before. Sign in again to see the list saved to your account.') : (copy.guestText || 'This list lives in this browser for now. Sign in and it follows you to every device.')) + '</p>'
          + '<a class="ajm-wl-btn ajm-wl-btn--outline" href="' + esc(ACCOUNT_URL) + '">' + esc(copy.signinLabel || 'Sign in') + '</a></div>';
      }
      if (!list.loggedIn) {
        if (!list.hasEmail && items.length) {
          tools += '<div class="ajm-wl-tool"><p class="ajm-wl-tool__title">' + esc(copy.emailTitle || 'Price-drop emails') + '</p>'
            + '<p>' + esc(copy.emailText || 'Leave an email and we\'ll let you know if a saved piece is reduced.') + '</p>'
            + '<form class="ajm-wl-email"><input type="email" required placeholder="Your email" aria-label="Your email" autocomplete="email"><button type="submit" class="ajm-wl-btn">Notify me</button></form></div>';
        }
      }
      if (items.length) {
        tools += '<div class="ajm-wl-tool ajm-wl-share"><p class="ajm-wl-tool__title">' + esc(copy.shareTitle || 'Share this list') + '</p>'
          + '<p>' + esc(copy.shareText || 'Anyone with the link sees the pieces you\'ve saved, nothing else.') + '</p>'
          + '<div class="ajm-wl-share__link"><input type="text" readonly value="' + esc(list.shareEnabled ? (list.shareUrl || '') : '') + '" placeholder="Your link is made when you copy or share it" aria-label="Link to your wishlist"><button type="button" class="ajm-wl-btn ajm-wl-btn--outline ajm-wl-share__copy">Copy</button></div>'
          + '<div class="ajm-wl-share__ways">'
          + (navigator.share ? '<button type="button" class="ajm-wl-linkbtn ajm-wl-share__sheet">Share…</button>' : '')
          + '<button type="button" class="ajm-wl-linkbtn ajm-wl-share__email-toggle" aria-expanded="false">Email it to someone</button>'
          + '</div>'
          + '<p class="ajm-wl-share__private"' + (list.shareEnabled ? '' : ' hidden') + '>' + esc(copy.privateText || 'Your list is shareable via the link above until you make it private.') + ' <button type="button" class="ajm-wl-linkbtn ajm-wl-share__private-btn">' + esc(copy.privateLabel || 'Make list private') + '</button></p>'
          + '<form class="ajm-wl-share__email" hidden>'
          + '<label><span>Their email</span><input type="email" name="to" required autocomplete="off" placeholder="friend@example.com"></label>'
          + '<label><span>Your name</span><input type="text" name="name" maxlength="80" autocomplete="name" placeholder="So they know who it is from"></label>'
          + '<label><span>A note <em>(optional)</em></span><textarea name="note" rows="2" maxlength="300" placeholder="Thought you would like these."></textarea></label>'
          + '<div class="ajm-wl-share__email-actions"><button type="submit" class="ajm-wl-btn">Send</button><span class="ajm-wl-share__email-status" role="status"></span></div>'
          + '</form></div>';
      }
      if (tools) html += '<div class="ajm-wl-tools">' + tools + '</div>';
      html += '<p class="ajm-wl-staff-note">' + esc(copy.staffNote || 'Pieces you save are kept on file with our staff, so shopping with us in person is easier. Remove a piece any time.') + '</p>';
      root.innerHTML = html;

      root.querySelectorAll('.ajm-wl-card').forEach(function (card, i) { patchCardLive(card, items[i]); });
      relay('wl-page', null, { detail: items.length ? 'own' : 'empty' });
      wirePieceClicks(root, 'own');
      wireAsk(root);

      root.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-remove]');
        if (rm) {
          var card = rm.closest('.ajm-wl-card');
          card.classList.add('is-removing');
          api('remove', { productId: rm.getAttribute('data-remove') }).then(function () {
            track('Remove', card.querySelector('.ajm-wl-card__title').textContent);
            card.remove();
            var left = root.querySelectorAll('.ajm-wl-card').length;
            root.querySelector('.ajm-wl-count').textContent = left ? left + (left === 1 ? ' piece saved' : ' pieces saved') : '';
            if (!left) renderPage(root);
          }).catch(function () { card.classList.remove('is-removing'); });
          return;
        }
        var sheetBtn = e.target.closest('.ajm-wl-share__sheet');
        if (sheetBtn) {
          ensureShared(root).then(function (url) {
            if (!url || !navigator.share) return;
            return navigator.share({ title: 'My wishlist at A.J. Martin', text: 'A few pieces I have my eye on at A.J. Martin Estate Jewelry.', url: url });
          }).then(function () { track('Share sheet'); relay('wl-share', null, { detail: 'sheet' }); }).catch(function () { /* cancelled or unavailable */ });
          return;
        }
        var emailToggle = e.target.closest('.ajm-wl-share__email-toggle');
        if (emailToggle) {
          var form = root.querySelector('.ajm-wl-share__email');
          var open = form.hidden;
          form.hidden = !open;
          emailToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) { var first = form.querySelector('input[name="to"]'); if (first) first.focus(); }
          return;
        }
        var copyBtn = e.target.closest('.ajm-wl-share__copy');
        if (copyBtn) {
          var input = root.querySelector('.ajm-wl-share__link input');
          var done = function () { copyBtn.textContent = 'Copied'; setTimeout(function () { copyBtn.textContent = 'Copy'; }, 2000); };
          ensureShared(root).then(function (url) {
            if (!url) return;
            relay('wl-share', null, { detail: 'copy' });
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { input.select(); });
            else { input.select(); try { document.execCommand('copy'); done(); } catch (err) { /* leave selected */ } }
          }).catch(function () { /* nothing to copy */ });
          return;
        }
        var privBtn = e.target.closest('.ajm-wl-share__private-btn');
        if (privBtn) {
          privBtn.disabled = true;
          api('share', { enabled: false }).then(function () {
            showShareState(root, false, '');
            track('Share off');
            relay('wl-share', null, { detail: 'off' });
          }).catch(function () { /* still shared; the line stays */ }).then(function () { privBtn.disabled = false; });
        }
      });
      var shareForm = root.querySelector('.ajm-wl-share__email');
      if (shareForm) {
        shareForm.addEventListener('submit', function (e) {
          e.preventDefault();
          var statusEl = shareForm.querySelector('.ajm-wl-share__email-status');
          var sendBtn = shareForm.querySelector('button[type="submit"]');
          var to = shareForm.querySelector('input[name="to"]').value.trim();
          var name = shareForm.querySelector('input[name="name"]').value.trim();
          var note = shareForm.querySelector('textarea[name="note"]').value.trim();
          sendBtn.disabled = true;
          statusEl.className = 'ajm-wl-share__email-status';
          statusEl.textContent = 'Sending…';
          api('share-email', { to: to, name: name, note: note }).then(function (r) {
            // sending switches sharing on, so the link row follows
            if (r.shareUrl) showShareState(root, true, r.shareUrl);
            statusEl.textContent = 'Sent to ' + to + '.';
            statusEl.classList.add('is-done');
            shareForm.querySelector('input[name="to"]').value = '';
            shareForm.querySelector('textarea[name="note"]').value = '';
            track('Share email');
            relay('wl-share', null, { detail: 'email' });
          }).catch(function (err) {
            statusEl.textContent = err.message || 'Could not send — please try again.';
            statusEl.classList.add('is-error');
          }).then(function () { sendBtn.disabled = false; });
        });
      }
      var emailForm = root.querySelector('.ajm-wl-email');
      if (emailForm) {
        emailForm.addEventListener('submit', function (e) {
          e.preventDefault();
          var input = emailForm.querySelector('input');
          api('email', { email: input.value }).then(function () {
            state.hasEmail = true;
            track('Email saved');
            relay('wl-email', null, { detail: 'page' });
            emailForm.innerHTML = '<p class="ajm-wl-email__done">Thank you. We\'ll email you if a saved piece is reduced.</p>';
          }).catch(function (err) {
            input.setCustomValidity(err.message || 'Please check the address');
            input.reportValidity();
            setTimeout(function () { input.setCustomValidity(''); }, 2000);
          });
        });
      }
    }).catch(function (err) {
      root.innerHTML = '<div class="ajm-wl-empty"><p>' + esc(err.message || 'Could not load your wishlist — please try again.') + '</p></div>';
    });
  }

  /* ---- boot ---- */
  /* ---- the drawer (2026-10-19, owner: Swym's red heart at the bottom left of every page opened a
     drawer from the left — "shows your list and allows you to browse without having to take you off
     page"). One floating heart with the count, one <aside> drawer built on first open, the same
     list the page shows in a narrower shape: photo, title, live price, Remove. Hidden on the
     wishlist page itself, where the full list is the page. ---- */
  var drawerEl, fabEl, backdropEl, drawerOpen = false, lastFocus = null;
  function mountDrawer() {
    if (fabEl || document.querySelector('[data-ajm-wishlist-page]')) return;
    fabEl = document.createElement('button');
    fabEl.type = 'button';
    fabEl.className = 'ajm-wish-fab';
    fabEl.setAttribute('aria-label', 'Your wishlist');
    fabEl.setAttribute('aria-haspopup', 'dialog');
    fabEl.setAttribute('aria-expanded', 'false');
    fabEl.innerHTML = '<span class="ajm-wish-fab__heart" aria-hidden="true"></span><span class="ajm-wish-fab__count ajm-wish-count"></span>';
    fabEl.addEventListener('click', function () { if (drawerOpen) closeDrawer(); else openDrawer('heart'); });
    document.body.appendChild(fabEl);
    paint(); // the count bubble
  }
  function ensureDrawer() {
    if (drawerEl) return drawerEl;
    backdropEl = document.createElement('div');
    backdropEl.className = 'ajm-wl-backdrop';
    backdropEl.addEventListener('click', closeDrawer);
    drawerEl = document.createElement('aside');
    drawerEl.className = 'ajm-wl-drawer';
    drawerEl.setAttribute('role', 'dialog');
    drawerEl.setAttribute('aria-modal', 'true');
    drawerEl.setAttribute('aria-label', 'Your wishlist');
    drawerEl.setAttribute('tabindex', '-1'); // focus lands on the dialog itself, not on a ringed Close button
    drawerEl.innerHTML = '<div class="ajm-wl-drawer__head"><h2 class="ajm-wl-drawer__title">Your wishlist</h2><p class="ajm-wl-drawer__count"></p><button type="button" class="ajm-wl-drawer__close" aria-label="Close">&times;</button></div>'
      + '<div class="ajm-wl-drawer__body"><p class="ajm-wl-loading">Loading…</p></div>'
      + '<div class="ajm-wl-drawer__foot"></div>';
    drawerEl.querySelector('.ajm-wl-drawer__close').addEventListener('click', closeDrawer);
    drawerEl.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-drawer-remove]');
      if (rm) {
        var row = rm.closest('.ajm-wl-row');
        row.classList.add('is-removing');
        api('remove', { productId: rm.getAttribute('data-drawer-remove') }).then(function () {
          track('Remove', row.querySelector('.ajm-wl-row__title').textContent);
          relay('wl-remove', { productId: rm.getAttribute('data-drawer-remove'), title: row.querySelector('.ajm-wl-row__title').textContent }, { where: 'drawer', detail: state.loggedIn ? 'account' : 'guest' });
          row.remove();
          renderDrawerCount(drawerEl.querySelectorAll('.ajm-wl-row').length);
          if (!drawerEl.querySelector('.ajm-wl-row')) renderDrawerEmpty();
        }).catch(function () { row.classList.remove('is-removing'); });
        return;
      }
      var a = e.target.closest('.ajm-wl-row a[href]');
      if (a) {
        var r = a.closest('.ajm-wl-row');
        relay('wl-click', { productId: r.getAttribute('data-product-id'), title: r.querySelector('.ajm-wl-row__title').textContent }, { detail: 'drawer' });
      }
    });
    document.body.appendChild(backdropEl);
    document.body.appendChild(drawerEl);
    return drawerEl;
  }
  function renderDrawerCount(n) {
    drawerEl.querySelector('.ajm-wl-drawer__count').textContent = n ? n + (n === 1 ? ' piece saved' : ' pieces saved') : '';
  }
  function renderDrawerEmpty() {
    drawerEl.querySelector('.ajm-wl-drawer__body').innerHTML = '<div class="ajm-wl-drawer__empty"><p>Nothing saved yet. Tap the heart on any piece to keep it here.</p><a class="ajm-wl-btn ajm-wl-btn--outline" href="' + esc(SHOP_URL) + '">Browse the store</a></div>';
    drawerEl.querySelector('.ajm-wl-drawer__foot').innerHTML = '';
    renderDrawerCount(0);
  }
  function drawerRowHtml(item) {
    var href = item.url || (item.handle ? '/products/' + item.handle : '#');
    var img = item.imageUrl ? '<img src="' + esc(imageAt(item.imageUrl, 200)) + '" alt="" loading="lazy">' : '<span class="ajm-wl-card__noimg"></span>';
    return '<li class="ajm-wl-row" data-product-id="' + esc(item.productId) + '" data-handle="' + esc(item.handle) + '" data-variant-id="' + esc(item.variantId) + '">'
      + '<a class="ajm-wl-row__image" href="' + esc(href) + '" tabindex="-1" aria-hidden="true">' + img + '</a>'
      + '<div class="ajm-wl-row__body">'
      + (item.addedBy === 'shop' ? '<p class="ajm-wl-row__from">Picked for you</p>' : '')
      + '<a class="ajm-wl-row__title" href="' + esc(href) + '">' + esc(item.title) + '</a>'
      + '<p class="ajm-wl-row__price">' + (item.priceAtSave != null ? '<span class="ajm-wl-row__now">' + esc(money(item.priceAtSave)) + '</span>' : '') + '<span class="ajm-wl-row__flag" hidden></span></p>'
      + '<button type="button" class="ajm-wl-row__remove" data-drawer-remove="' + esc(item.productId) + '">Remove</button>'
      + '</div></li>';
  }
  function patchRowLive(row, item) {
    liveProduct(item.handle).then(function (p) {
      if (!p) return;
      var variant = null;
      if (item.variantId && Array.isArray(p.variants)) {
        for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(item.variantId)) variant = p.variants[i];
      }
      var available = variant ? variant.available : p.available;
      var cents = variant ? variant.price : p.price;
      var now = typeof cents === 'number' ? cents / 100 : null;
      var flag = row.querySelector('.ajm-wl-row__flag');
      var priceEl = row.querySelector('.ajm-wl-row__price');
      if (!available) {
        row.classList.add('is-sold'); flag.textContent = 'Sold'; flag.hidden = false;
        similarTo(item.productId, 2).then(function (list) {
          var body = row.querySelector('.ajm-wl-row__body');
          if (body && list.length && !row.querySelector('.ajm-wl-similar')) body.insertAdjacentHTML('beforeend', similarHtml(list, true));
        });
        return;
      }
      if (now != null && item.priceAtSave != null && now < item.priceAtSave - 0.005) {
        priceEl.innerHTML = '<span class="ajm-wl-row__now">' + esc(money(now)) + '</span> <s class="ajm-wl-row__was">' + esc(money(item.priceAtSave)) + '</s><span class="ajm-wl-row__flag">Price reduced</span>';
      } else if (now != null && item.priceAtSave == null) {
        priceEl.innerHTML = '<span class="ajm-wl-row__now">' + esc(money(now)) + '</span><span class="ajm-wl-row__flag" hidden></span>';
      }
    });
  }
  function openDrawer(from) {
    ensureDrawer();
    lastFocus = document.activeElement;
    drawerOpen = true;
    drawerEl.classList.add('is-open');
    backdropEl.classList.add('is-open');
    document.documentElement.classList.add('ajm-wl-drawer-open');
    if (fabEl) fabEl.setAttribute('aria-expanded', 'true');
    drawerEl.focus({ preventScroll: true });
    track('Drawer open', from || '');
    relay('wl-drawer', null, { detail: from || 'heart' });
    var body = drawerEl.querySelector('.ajm-wl-drawer__body');
    body.innerHTML = '<p class="ajm-wl-loading">Loading…</p>';
    api('list').then(function (list) {
      var items = list.items || [];
      if (!items.length) { renderDrawerEmpty(); return; }
      body.innerHTML = '<ul class="ajm-wl-rows">' + items.map(drawerRowHtml).join('') + '</ul>';
      body.querySelectorAll('.ajm-wl-row').forEach(function (row, i) { patchRowLive(row, items[i]); });
      renderDrawerCount(items.length);
      drawerEl.querySelector('.ajm-wl-drawer__foot').innerHTML = '<a class="ajm-wl-btn" href="' + esc(PAGE_URL) + '">View your wishlist</a>'
        + '<a class="ajm-wl-drawer__ask" href="' + esc(PAGE_URL) + '#ask">Ask about these, or see them in person</a>'
        + (!list.loggedIn ? '<p class="ajm-wl-drawer__hint">This list lives in this browser. <a href="' + esc(ACCOUNT_URL) + '">Sign in</a> and it follows you to every device.</p>' : '');
    }).catch(function (err) {
      body.innerHTML = '<p class="ajm-wl-drawer__hint">' + esc(err.message || 'Could not load your wishlist — please try again.') + '</p>';
    });
  }
  function closeDrawer() {
    if (!drawerOpen) return;
    drawerOpen = false;
    drawerEl.classList.remove('is-open');
    backdropEl.classList.remove('is-open');
    document.documentElement.classList.remove('ajm-wl-drawer-open');
    if (fabEl) fabEl.setAttribute('aria-expanded', 'false');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawerOpen) closeDrawer(); });
  /* Menu links (2026-10-19, owner: Services › Wishlist should open the slider): any link to
     /pages/wishlist#drawer opens the drawer in place. A menu can only hold a URL, and this one
     still lands on the wishlist page if the script has not loaded. On the wishlist page itself
     the page is the list, so the link just stays put. Arriving with #drawer opens it too. */
  function isDrawerLink(a) {
    if (!a || !a.getAttribute) return false;
    try {
      var u = new URL(a.getAttribute('href'), location.href);
      return u.hash === '#drawer' && u.origin === location.origin && u.pathname.replace(/\/$/, '') === PAGE_URL.replace(/\/$/, '');
    } catch (err) { return false; }
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!isDrawerLink(a)) return;
    e.preventDefault();
    if (document.querySelector('[data-ajm-wishlist-page]')) return;
    openDrawer('menu');
  }, true);
  document.addEventListener('click', function (e) {
    var open = e.target.closest && e.target.closest('[data-wl-open]');
    if (!open) return;
    e.preventDefault();
    hideToast();
    openDrawer(open.closest('.ajm-wish-toast') ? 'toast' : 'link');
  });

  function boot() {
    loadCache();
    paint();
    mountDrawer();
    if (location.hash === '#drawer' && !document.querySelector('[data-ajm-wishlist-page]')) openDrawer('menu');
    observer.observe(document.body, { childList: true, subtree: true });
    var page = document.querySelector('[data-ajm-wishlist-page]');
    if (page) renderPage(page); // the list call also refreshes state
    else api('state').catch(function () { /* hearts keep the cached state */ });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.AJMWishlist = {
    state: state,
    isSaved: isSaved,
    toggle: toggle,
    refresh: function () { return api('state'); },
    renderPage: renderPage,
    toast: toast,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer
  };
})();
